/** @file Sync Konva token shapes with state. */
import Konva from "konva";
import { appStore } from "../core/state.js";
import { metersToPx, pxToMeters } from "../core/coords.js";
import { execute } from "../core/history.js";
import { moveTokenCommand } from "../core/commands.js";
import { tokenFillColor, tokenStrokeColor } from "../core/play.js";
import { markDirty } from "./renderer.js";

const TOKEN_RADIUS_M = 0.55;
const MIN_TOKEN_RADIUS_PX = 18; // keeps a usable touch target without crowding dense scenes
const BALL_RADIUS_M = 0.35;
const MIN_BALL_RADIUS_PX = 14;

/** @typedef {import("../core/play.js").Token} Token */

/** @type {Map<string, Konva.Group>} */
const nodes = new Map();
/** @type {Konva.Layer | null} */
let layer = null;
/** @type {(() => void) | null} */
let unsubStore = null;
/** @type {(info: { tokenId: string }) => void} */
let tokenTapHandler = () => {};

/**
 * @param {Konva.Layer} l
 * @param {(info: { tokenId: string }) => void} onTokenTap
 */
export function mountTokens(l, onTokenTap) {
  layer = l;
  tokenTapHandler = onTokenTap;
  syncAll();
  unsubStore = appStore.subscribe((next, prev) => {
    if (next.tokens !== prev.tokens || next.transform !== prev.transform) {
      syncAll();
    }
  });
}

export function unmountTokens() {
  unsubStore?.();
  unsubStore = null;
  for (const node of nodes.values()) {
    node.destroy();
  }
  nodes.clear();
  layer = null;
}

function syncAll() {
  const state = appStore.get();
  if (!layer || !state.transform) {
    return;
  }

  const seen = new Set();
  for (const token of state.tokens) {
    seen.add(token.id);
    const node = nodes.get(token.id) ?? createNode(token);
    nodes.set(token.id, node);
    updateNode(node, token);
    if (!node.getLayer()) {
      layer.add(node);
    }
  }

  for (const [id, node] of nodes) {
    if (!seen.has(id)) {
      node.destroy();
      nodes.delete(id);
    }
  }

  markDirty(layer);
}

/**
 * @param {Token} token
 * @returns {Konva.Group}
 */
function createNode(token) {
  const group = new Konva.Group({
    id: token.id,
    draggable: true,
  });

  const circle = new Konva.Circle({
    name: "body",
    radius: 1,
  });
  group.add(circle);

  if (token.kind === "ball") {
    const accent = new Konva.Line({
      name: "ball-accent",
      points: [0, 0, 0, 0],
      stroke: "#111827",
      strokeWidth: 2,
    });
    group.add(accent);
  } else {
    const number = new Konva.Text({
      name: "number",
      text: String(token.number),
      fontFamily: "system-ui, sans-serif",
      fontStyle: "bold",
      fill: "#ffffff",
      align: "center",
      verticalAlign: "middle",
    });
    group.add(number);

    const label = new Konva.Text({
      name: "label",
      text: token.label ?? "",
      fontFamily: "system-ui, sans-serif",
      fill: "#ffffff",
      align: "center",
      verticalAlign: "middle",
    });
    group.add(label);
  }

  group.on("dragend", () => {
    const state = appStore.get();
    const t = state.transform;
    const current = state.tokens.find((tok) => tok.id === token.id);
    if (!t || !current) {
      return;
    }
    const meters = pxToMeters(t, { px: group.x(), py: group.y() });
    const clamped = clampToField(meters);
    if (clamped.x === current.x && clamped.y === current.y) {
      // Snap visual back if no meaningful change
      const p = metersToPx(t, current);
      group.position({ x: p.px, y: p.py });
      const activeLayer = layer ?? group.getLayer();
      if (activeLayer) {
        markDirty(activeLayer);
      }
      return;
    }
    execute(moveTokenCommand(token.id, { x: current.x, y: current.y }, clamped));
  });

  group.on("tap click", () => {
    if (appStore.get().tool === "select") {
      tokenTapHandler({ tokenId: token.id });
    }
  });

  return group;
}

/**
 * @param {Konva.Group} group
 * @param {Token} token
 */
function updateNode(group, token) {
  const state = appStore.get();
  const t = state.transform;
  if (!t) {
    return;
  }

  const pos = metersToPx(t, { x: token.x, y: token.y });
  group.position({ x: pos.px, y: pos.py });

  const isBall = token.kind === "ball";
  const radiusPx = Math.max(
    isBall ? MIN_BALL_RADIUS_PX : MIN_TOKEN_RADIUS_PX,
    (isBall ? BALL_RADIUS_M : TOKEN_RADIUS_M) * t.pxPerMeter,
  );

  const circle = /** @type {Konva.Circle | undefined} */ (group.findOne(".body"));
  if (circle) {
    circle.radius(radiusPx);
    circle.fill(tokenFillColor(token));
    circle.stroke(tokenStrokeColor(token));
    circle.strokeWidth(token.kind === "goalkeeper" ? 4 : 3);
    circle.shadowColor(state.selectedDrawingId ? "transparent" : "rgba(0,0,0,0.45)");
    circle.shadowBlur(isBall ? 4 : 6);
    circle.shadowOffsetY(2);
  }

  if (isBall) {
    const accent = /** @type {Konva.Line | undefined} */ (group.findOne(".ball-accent"));
    if (accent) {
      const r = radiusPx * 0.8;
      accent.points([-r, 0, r, 0]);
    }
  } else {
    const number = /** @type {Konva.Text | undefined} */ (group.findOne(".number"));
    if (number) {
      number.text(String(token.number));
      const size = Math.max(12, radiusPx * 0.85);
      number.fontSize(size);
      number.width(radiusPx * 2);
      number.height(radiusPx * 2);
      number.offset({ x: radiusPx, y: radiusPx + size * 0.05 });
    }

    const label = /** @type {Konva.Text | undefined} */ (group.findOne(".label"));
    if (label) {
      label.text(token.label ?? "");
      const size = Math.max(11, radiusPx * 0.55);
      label.fontSize(size);
      label.fontStyle("bold");
      label.fill("#1e293b");
      label.stroke("");
      label.strokeWidth(0);
      label.width(radiusPx * 4);
      label.offset({ x: radiusPx * 2, y: -radiusPx - size * 0.5 });
    }
  }
}

/**
 * @param {{x:number,y:number}} m
 * @returns {{x:number,y:number}}
 */
function clampToField(m) {
  return {
    x: Math.min(40, Math.max(0, Number(m.x.toFixed(3)))),
    y: Math.min(20, Math.max(0, Number(m.y.toFixed(3)))),
  };
}

/**
 * Expose for input layer: check if a pixel hit a token via Konva hit detection.
 * @param {Konva.Stage} stage
 * @param {{px:number,py:number}} pxPoint
 * @returns {string | null} token id or null
 */
export function tokenIdAt(stage, pxPoint) {
  const shape = stage.getIntersection({ x: pxPoint.px, y: pxPoint.py });
  if (!shape) {
    return null;
  }
  const group = shape.getParent();
  const id = group?.id();
  if (!id) {
    return null;
  }
  return nodes.has(id) ? id : null;
}
